import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../constants/theme';
import { useAppShellPlatform } from '../../hooks/useAppShellPlatform';
import { FilterBlock } from '../intel/FilterBlock';
import { RangeControl } from '../intel/RangeControl';
import { TARGET_FINDER_RACES as RACES, TARGET_FINDER_RANKS as RANKS, TARGET_FINDER_PRESETS } from '../../constants/targetFinderOptions';
import { formatEnumLabel } from '../../utils/format';
import type { TargetFilterState } from '../../utils/targetSearchParams';
import type { PlayerStats } from '../../api/intel';

interface MobileTargetFilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
  filters: TargetFilterState;
  playerStats: PlayerStats | null;
  onApply: (filters: TargetFilterState) => void;
}

type RangeDim = 'nw' | 'land' | 'nwpa' | 'kdNw' | 'kdLand';

const RANGE_DIM_CONFIG: Record<RangeDim, { label: string; refLabel?: string; refValue: (s: PlayerStats | null) => number | null }> = {
  nw: { label: 'Province Networth', refLabel: 'gc', refValue: (s) => s?.networth ?? null },
  nwpa: { label: 'NW/Acre', refValue: (s) => s?.nwpa ?? null },
  land: { label: 'Province Land', refLabel: 'acres', refValue: (s) => s?.land ?? null },
  kdNw: { label: 'Kingdom Networth', refLabel: 'gc', refValue: (s) => s?.kingdomNW ?? null },
  kdLand: { label: 'Kingdom Land', refLabel: 'acres', refValue: (s) => s?.kingdomLand ?? null },
};

function activePresetIndex(min: string, max: string): number {
  const idx = TARGET_FINDER_PRESETS.findIndex((p) => p.lower !== null && String(p.lower) === min && String(p.upper) === max);
  return idx === -1 ? TARGET_FINDER_PRESETS.length - 1 : idx;
}

/**
 * SF-171 Component 6: full-screen bottom sheet covering all 7 of desktop's filter dimensions —
 * Province (race, rank, networth, NW/acre, land) and Kingdom (kingdom networth, kingdom land), as
 * two labeled sections. Edits a local **draft** copy of `filters`, not the page state directly —
 * closing via the header X / backdrop / Escape discards the draft; "Apply & Search" commits it via
 * `onApply` (which also triggers the search, page-owned — see `TargetFinder.tsx`). A11y mirrors
 * the existing `ResourceBarSheet.tsx` precedent: focus trap, Escape-to-close, body-scroll-lock,
 * breakpoint-flip auto-close guard.
 */
export function MobileTargetFilterSheet({ isOpen, onClose, filters, playerStats, onApply }: MobileTargetFilterSheetProps) {
  const [draft, setDraft] = useState<TargetFilterState>(filters);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const platform = useAppShellPlatform();

  // Re-seed the draft from the last-committed filters every time the sheet opens.
  useEffect(() => {
    if (isOpen) setDraft(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    triggerRef.current = document.activeElement;

    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'button, input, select, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusableEls = panelRef.current.querySelectorAll<HTMLElement>(
          'button, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableEls.length === 0) return;
        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // The sheet's sole trigger only exists below lg:, but nothing stops a viewport crossing to lg:+
  // while it's already open (SF-129 fixed-overlay hazard).
  useEffect(() => {
    if (isOpen && platform === 'desktop') {
      onClose();
    }
  }, [isOpen, platform, onClose]);

  if (!isOpen) return null;

  function toggleChip(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  function rangeProps(dim: RangeDim) {
    const cfg = RANGE_DIM_CONFIG[dim];
    const minKey = `${dim}Min` as keyof TargetFilterState;
    const maxKey = `${dim}Max` as keyof TargetFilterState;
    const min = draft[minKey] as string;
    const max = draft[maxKey] as string;
    return {
      refValue: cfg.refValue(playerStats),
      refLabel: cfg.refLabel,
      presets: TARGET_FINDER_PRESETS,
      activePreset: activePresetIndex(min, max),
      onPresetSelect: (i: number) => {
        const p = TARGET_FINDER_PRESETS[i];
        if (p.lower != null) {
          setDraft((d) => ({ ...d, [minKey]: String(p.lower), [maxKey]: String(p.upper) }));
        }
      },
      lowerVal: min,
      upperVal: max,
      onLower: (v: string) => setDraft((d) => ({ ...d, [minKey]: v })),
      onUpper: (v: string) => setDraft((d) => ({ ...d, [maxKey]: v })),
      unit: '%',
      size: 'touch' as const,
    };
  }

  return (
    <>
      <div
        data-testid="target-filter-sheet-backdrop"
        className="fixed inset-0 bg-black bg-opacity-70"
        style={{ zIndex: 50 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="target-filter-sheet-title"
        className="fixed inset-0 flex flex-col"
        style={{ zIndex: 50, backgroundColor: COLORS.bg }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}` }}>
          <h2 id="target-filter-sheet-title" style={{ fontSize: 16, fontWeight: 700, color: COLORS.text.primary, margin: 0 }}>
            Filters
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: COLORS.text.tertiary, fontSize: 20, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {playerStats === null && (
            <p style={{ color: COLORS.text.muted, fontSize: 11, marginBottom: 12 }}>
              Percent-based presets need a completed search first — run a search, then adjust filters.
            </p>
          )}

          <h3 style={{ fontSize: 12, fontWeight: 700, color: COLORS.link, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Province Filters
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
            <FilterBlock label="Race" enabled={draft.raceEnabled} onToggle={(v) => setDraft((d) => ({ ...d, raceEnabled: v }))}>
              <ChipGroup options={RACES} selected={draft.raceSelected} onToggle={(v) => setDraft((d) => ({ ...d, raceSelected: toggleChip(d.raceSelected, v) }))} />
            </FilterBlock>
            <FilterBlock label="Rank (Honour)" enabled={draft.rankEnabled} onToggle={(v) => setDraft((d) => ({ ...d, rankEnabled: v }))}>
              <ChipGroup options={RANKS} selected={draft.rankSelected} onToggle={(v) => setDraft((d) => ({ ...d, rankSelected: toggleChip(d.rankSelected, v) }))} />
            </FilterBlock>
            <FilterBlock label="Province Networth" enabled={draft.nwEnabled} onToggle={(v) => setDraft((d) => ({ ...d, nwEnabled: v }))}>
              <RangeControl {...rangeProps('nw')} />
            </FilterBlock>
            <FilterBlock label="NW/Acre" enabled={draft.nwpaEnabled} onToggle={(v) => setDraft((d) => ({ ...d, nwpaEnabled: v }))}>
              <RangeControl {...rangeProps('nwpa')} />
            </FilterBlock>
            <FilterBlock label="Province Land" enabled={draft.landEnabled} onToggle={(v) => setDraft((d) => ({ ...d, landEnabled: v }))}>
              <RangeControl {...rangeProps('land')} />
            </FilterBlock>
          </div>

          <h3 style={{ fontSize: 12, fontWeight: 700, color: COLORS.gold, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Kingdom Filters
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FilterBlock label="Kingdom Networth" enabled={draft.kdNwEnabled} onToggle={(v) => setDraft((d) => ({ ...d, kdNwEnabled: v }))}>
              <RangeControl {...rangeProps('kdNw')} />
            </FilterBlock>
            <FilterBlock label="Kingdom Land" enabled={draft.kdLandEnabled} onToggle={(v) => setDraft((d) => ({ ...d, kdLandEnabled: v }))}>
              <RangeControl {...rangeProps('kdLand')} />
            </FilterBlock>
          </div>
        </div>

        <div style={{ padding: 16, borderTop: `1px solid ${COLORS.border}` }}>
          <button
            type="button"
            onClick={() => onApply(draft)}
            style={{
              width: '100%', minHeight: 44, borderRadius: 6, border: 'none',
              background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Apply & Search
          </button>
        </div>
      </div>
    </>
  );
}

function ChipGroup({ options, selected, onToggle }: { options: string[]; selected: string[]; onToggle: (value: string) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((opt) => {
        const isSelected = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onToggle(opt)}
            style={{
              minHeight: 44,
              padding: '0 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              background: isSelected ? COLORS.link : 'transparent',
              color: isSelected ? '#fff' : COLORS.text.secondary,
              border: `1px solid ${isSelected ? COLORS.link : COLORS.border}`,
            }}
          >
            {formatEnumLabel(opt)}
          </button>
        );
      })}
    </div>
  );
}
